/**
 * Type-safe data persistence and storage utilities for the German Verb Trainer
 */

export interface StorageUsage {
  totalSize: number;
  totalKeys: number;
  keys: string[];
}

export interface ExportData {
  verbName: string;
  exportDate: string;
  data: Record<string, any>;
}

export interface MigrationResult {
  migratedCount: number;
  migrations: string[];
}

/**
 * Type-safe storage manager with localStorage persistence
 */
export class StorageManager {
  private readonly verbName: string;
  private readonly prefix: string;

  constructor(verbName: string) {
    this.verbName = verbName;
    this.prefix = `${verbName}-trainer`;
  }

  /**
   * Save data to localStorage with error handling and type safety
   * @param key - The storage key
   * @param data - The data to save (will be JSON serialized)
   * @returns Success status
   */
  save<T>(key: string, data: T): boolean {
    try {
      const fullKey = `${this.prefix}-${key}`;
      localStorage.setItem(fullKey, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error(`Error saving ${key} to localStorage:`, error);
      return false;
    }
  }

  /**
   * Load data from localStorage with error handling and type safety
   * @param key - The storage key
   * @param defaultValue - Default value if key doesn't exist
   * @returns The loaded data or default value
   */
  load<T>(key: string, defaultValue: T | null = null): T | null {
    try {
      const fullKey = `${this.prefix}-${key}`;
      const item = localStorage.getItem(fullKey);
      return item ? (JSON.parse(item) as T) : defaultValue;
    } catch (error) {
      console.error(`Error loading ${key} from localStorage:`, error);
      return defaultValue;
    }
  }

  /**
   * Remove data from localStorage
   * @param key - The storage key to remove
   * @returns Success status
   */
  remove(key: string): boolean {
    try {
      const fullKey = `${this.prefix}-${key}`;
      localStorage.removeItem(fullKey);
      return true;
    } catch (error) {
      console.error(`Error removing ${key} from localStorage:`, error);
      return false;
    }
  }

  /**
   * Check if localStorage is available
   * @returns Whether localStorage is available
   */
  isAvailable(): boolean {
    try {
      const test = "__storage_test__";
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Get storage usage statistics for this app
   * @returns Storage usage information
   */
  getStorageUsage(): StorageUsage {
    let totalSize = 0;
    const keys: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.prefix)) {
        const value = localStorage.getItem(key);
        totalSize += key.length + (value ? value.length : 0);
        keys.push(key);
      }
    }

    return {
      totalSize,
      totalKeys: keys.length,
      keys,
    };
  }

  /**
   * Clear all data for this app
   * @returns Number of keys removed
   */
  clearAll(): number {
    const usage = this.getStorageUsage();
    usage.keys.forEach((key) => {
      localStorage.removeItem(key);
    });
    return usage.totalKeys;
  }

  /**
   * Export all data as JSON
   * @returns Exported data object
   */
  exportData(): ExportData {
    const data: Record<string, any> = {};
    const usage = this.getStorageUsage();

    usage.keys.forEach((fullKey) => {
      const shortKey = fullKey.replace(`${this.prefix}-`, "");
      data[shortKey] = this.load(shortKey);
    });

    return {
      verbName: this.verbName,
      exportDate: new Date().toISOString(),
      data,
    };
  }

  /**
   * Import data from JSON export
   * @param exportedData - The exported data to import
   * @returns Number of keys imported
   * @throws Error if export data is invalid
   */
  importData(exportedData: ExportData): number {
    if (!exportedData || !exportedData.data) {
      throw new Error("Invalid export data format");
    }

    let importedKeys = 0;
    for (const [key, value] of Object.entries(exportedData.data)) {
      if (this.save(key, value)) {
        importedKeys++;
      }
    }

    return importedKeys;
  }

  /**
   * Migrate old data format to new format
   * @returns Migration result with count and details
   */
  migrate(): MigrationResult {
    const migrations = [
      { name: "form-stats", fn: this.migrateFormStats.bind(this) },
      { name: "achievements", fn: this.migrateAchievements.bind(this) },
      { name: "streaks", fn: this.migrateStreaks.bind(this) },
    ];

    const completedMigrations: string[] = [];
    migrations.forEach((migration) => {
      if (migration.fn()) {
        completedMigrations.push(migration.name);
      }
    });

    return {
      migratedCount: completedMigrations.length,
      migrations: completedMigrations,
    };
  }

  /**
   * Migrate form stats to new format
   * @private
   */
  private migrateFormStats(): boolean {
    const formStats = this.load<any>("form-stats");
    if (!formStats) return false;

    let migrated = false;
    for (const [_tense, pronounStats] of Object.entries(formStats)) {
      for (const [_pronoun, stats] of Object.entries(pronounStats as any)) {
        const statsObj = stats as any;
        // Add missing fields from old format
        if (!statsObj.hasOwnProperty("timeWeightedAccuracy")) {
          statsObj.timeWeightedAccuracy = statsObj.accuracy || 0;
          migrated = true;
        }
        if (!statsObj.hasOwnProperty("lastPracticed")) {
          statsObj.lastPracticed = Date.now();
          migrated = true;
        }
        if (!statsObj.hasOwnProperty("attempts")) {
          statsObj.attempts = [];
          migrated = true;
        }
      }
    }

    if (migrated) {
      this.save("form-stats", formStats);
    }

    return migrated;
  }

  /**
   * Migrate achievements to new format
   * @private
   */
  private migrateAchievements(): boolean {
    const achievements = this.load<any>("achievements");
    if (!achievements || !Array.isArray(achievements)) {
      // Convert old format or create new
      this.save("achievements", []);
      return true;
    }
    return false;
  }

  /**
   * Migrate streaks to new format
   * @private
   */
  private migrateStreaks(): boolean {
    const streaks = this.load<any>("streaks");
    if (!streaks) return false;

    let migrated = false;

    // Add missing fields
    if (!streaks.hasOwnProperty("longest")) {
      streaks.longest = Math.max(streaks.current || 0, streaks.daily || 0);
      migrated = true;
    }
    if (!streaks.hasOwnProperty("totalDays")) {
      streaks.totalDays = streaks.daily || 0;
      migrated = true;
    }

    if (migrated) {
      this.save("streaks", streaks);
    }

    return migrated;
  }
}

// Make StorageManager globally available with a different name to avoid conflict
declare global {
  interface Window {
    VerbStorageManager: typeof StorageManager;
  }
}

window.VerbStorageManager = StorageManager;
